'use client'

import { useState, type JSX } from 'react'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  ConversationActionProps,
  ConversationActionType,
} from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import { useSession } from '@marketplace-web/shared/session-data'

import BalanceActivationModal from '../BalanceActivationModal'

type Props = ConversationActionProps & {
  children: JSX.Element
  onSuccess: () => void
}

const EnsureBalanceActivated = ({
  action,
  children,
  title,
  primary,
  size,
  theme,
  inverse,
  onSuccess,
}: Props) => {
  const translate = useTranslate('conversation.actions.activate_balance')

  const { user, setHasConfirmedPaymentsAccount } = useSession()

  const [isActivateBalanceOpen, setIsActivateBalanceOpen] = useState(false)

  const showActivateBalanceModal = () => setIsActivateBalanceOpen(true)

  const hideActivateBalanceModal = () => setIsActivateBalanceOpen(false)

  const handleBalanceActivated = () => {
    hideActivateBalanceModal()
    setHasConfirmedPaymentsAccount(true)
    onSuccess()
  }

  const renderModal = () => {
    const labels = {
      title: translate('title'),
      description: translate('description'),
    }

    if (!isActivateBalanceOpen) return null

    return (
      <BalanceActivationModal
        labels={labels}
        isOpen={isActivateBalanceOpen}
        onSuccess={handleBalanceActivated}
        onCancel={hideActivateBalanceModal}
      />
    )
  }

  const renderActionButton = () => {
    return (
      <ConversationActionButton
        text={title}
        testId="ensure-balance-activated-action-button"
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={showActivateBalanceModal}
      />
    )
  }

  const mustActivateBalance =
    !user?.has_confirmed_payments_account && action !== ConversationActionType.ReturnOrder

  if (mustActivateBalance) {
    return (
      <>
        {renderActionButton()}
        {renderModal()}
      </>
    )
  }

  return children
}

export default EnsureBalanceActivated
